/**
 * CreateOnboardingWizardV2 Component
 *
 * Create onboarding wizard for V2 themes (ThemeV2)
 * instead of V1 themes. Shows V2 theme selection in Step 2 and
 * ThemeV2EditorView in Step 3.
 *
 * Steps:
 * 1. Basic Info (name, language, locales) — same as V1
 * 2. V2 Theme Selection — grid of V2 themes with FlowDocument previews
 * 3. V2 Theme Customization — ThemeV2EditorView with V2 screen editing
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  OutlinedInput,
  Alert,
  SelectChangeEvent,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Typography,
  Stack,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
  DEFAULT_ACTION_BAR,
  DEFAULT_BACKDROP,
  DEFAULT_HEADER_BAR,
  type CreateThemeV2Input,
  type ThemeV2,
} from '@modules/themes/types/themesV2.types';
import type { OnboardingThemeSettingsV2 } from '@modules/firebase-core/converters/onboardingConverter';
import type { StepNode } from '@segmently/schema-models/v2';
import { useThemesV2 } from '@modules/themes/hooks/useThemesV2';
import { useProjectThemes } from '@modules/themes/hooks/useProjectThemes';
import {
  ThemeV2SelectionList,
  type ThemeV2SelectionSource,
} from '@modules/themes/components/ThemeV2SelectionList';
import { ThemeV2EditorView } from '@modules/themes/components/ThemeV2EditorView';
import { GenerateThemeFromImageDialog } from '@modules/themes/components/GenerateThemeFromImageDialog';
import { GeneratedThemeMetadataPanel } from '@modules/themes/components/GeneratedThemeMetadataPanel';
import { createDefaultFlowDocument } from '@modules/themes/factories';
import {
  createEditableThemeV2FromDraft,
  themeV2ToOnboardingSettings,
} from '@modules/themes/utils';
import type { ProjectThemeV2 } from '@modules/firebase-core/services/ProjectThemeService';
import { OnboardingScreenEditorDialogV2 } from '@modules/reactFlow/components/onboarding/OnboardingScreenEditorDialogV2';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import { getLocalesForPlatform, isKnownLocale } from '@modules/locale-config';
import { useLocaleConfig } from '@modules/locale-config';
import type { OnboardingPlatform } from '@modules/locale-config';
import { WithGuideHelp } from '@modules/ui-components';
import { GuideService } from '@modules/firebase-core/services/GuideService';
import { GUIDE_ANCHORS } from '@modules/guides/anchors/guideAnchorRegistry';
import {
  FULLSCREEN_DIALOG_BUTTON_SX,
  FULLSCREEN_DIALOG_HEADER_SX,
  FULLSCREEN_DIALOG_PAPER_SX,
  FULLSCREEN_DIALOG_TITLE_SX,
} from '@modules/themes/components/fullscreenDialogChrome';

/**
 * Deep clone utility - uses structuredClone if available, falls back to JSON
 */
function deepClone<T>(obj: T): T {
  if (typeof structuredClone === 'function') {
    return structuredClone(obj);
  }
  return JSON.parse(JSON.stringify(obj));
}

function projectThemeToThemeV2(theme: ProjectThemeV2): ThemeV2 {
  return {
    id: theme.id,
    name: theme.name,
    colors: theme.colors,
    fonts: theme.fonts,
    optionsListSettings: theme.optionsListSettings,
    layoutSettings: theme.layoutSettings,
    animationSettings: theme.animationSettings,
    background: theme.background,
    headerBar: theme.headerBar,
    actionBar: theme.actionBar,
    generatedMetadata: theme.generatedMetadata,
    flowDocument: theme.flowDocument ?? createDefaultFlowDocument(),
    createdAt: theme.createdAt,
    updatedAt: theme.updatedAt,
  };
}

/**
 * Props for CreateOnboardingWizardV2
 */
export interface CreateOnboardingWizardV2Props {
  /** Whether dialog is open */
  open: boolean;
  /** Callback when dialog is closed */
  onClose: () => void;
  /** Callback when onboarding is created */
  onCreate: (data: {
    name: string;
    defaultLanguage: string;
    languages: string[];
    /** V2 Theme settings (stored in Onboarding subcollection) */
    themeSettingsV2?: OnboardingThemeSettingsV2 | null;
    /** Target platform */
    platform: OnboardingPlatform;
  }) => Promise<string>;
  /** Project ID (for loading custom locales from project settings) */
  projectId: string;
  /** Optional folder ID to create onboarding in */
  folderId?: string | null;
  /** First onboarding - prefill name with "New onboarding" */
  isFirstOnboarding?: boolean;
}

const STEPS = ['Basic Information', 'Select Theme', 'Customize Theme'];
const STEP_TITLES = ['Basic Funnel Info', 'Select Theme', 'Customize Theme'];
const WIZARD_STEP_GUIDE_ANCHORS = [
  GUIDE_ANCHORS.onboardingWizardBasicInfo,
  GUIDE_ANCHORS.onboardingWizardThemeSelect,
  GUIDE_ANCHORS.onboardingWizardThemeCustomize,
] as const;

/**
 * CreateOnboardingWizardV2 Component
 */
export function CreateOnboardingWizardV2({
  open,
  onClose,
  onCreate,
  projectId,
  isFirstOnboarding = false,
}: CreateOnboardingWizardV2Props) {
  const guideService = useMemo(() => new GuideService(), []);

  // Wizard state
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load project custom locales
  const { config: localeConfig } = useLocaleConfig(projectId);

  // Step 1 state
  const [name, setName] = useState('');
  const [platform, setPlatform] = useState<OnboardingPlatform>('web');
  const [defaultLanguage, setDefaultLanguage] = useState<string>('en-US');
  const [languages, setLanguages] = useState<string[]>(['en-US']);

  // Step 2 state — V2 themes
  const [selectedTheme, setSelectedTheme] = useState<ThemeV2 | null>(null);

  // Step 3 state — customized V2 theme (deep clone)
  const [customizedTheme, setCustomizedTheme] = useState<ThemeV2 | null>(null);
  const [generateOpen, setGenerateOpen] = useState(false);

  // Screen editor dialog state (Step 3)
  const [screenEditorOpen, setScreenEditorOpen] = useState(false);
  const [editingStep, setEditingStep] = useState<FlowStepNode | null>(null);

  // Key to force carousel refresh after screen save
  const [carouselKey, setCarouselKey] = useState(0);

  // Ref to store latest state values for callback (avoids stale closure)
  const wizardStateRef = useRef({
    customizedTheme,
    setCustomizedTheme,
    setCarouselKey,
    setScreenEditorOpen,
    setEditingStep,
  });

  // V2 Themes list with lazy loading. Product themes are shown first, then global themes.
  const { themes: globalThemes, loading: themesLoading, hasMore, loadMore } = useThemesV2({ pageSize: 50 });
  const { themes: projectThemes, loading: projectThemesLoading } = useProjectThemes(projectId);
  const projectThemeOptions = useMemo(
    () => projectThemes.map(projectThemeToThemeV2),
    [projectThemes]
  );
  const themeSources = useMemo<ThemeV2SelectionSource[]>(() => {
    const sources: ThemeV2SelectionSource[] = [];
    if (projectThemeOptions.length > 0) {
      sources.push({
        id: 'project',
        label: 'Product themes',
        themes: projectThemeOptions,
        themeSource: 'project',
        chipLabel: 'Product',
        chipColor: 'primary',
      });
    }
    sources.push({
      id: 'system',
      label: 'System themes',
      themes: globalThemes,
      themeSource: 'global',
      chipLabel: 'Global',
    });
    return sources;
  }, [globalThemes, projectThemeOptions]);
  const orderedThemes = useMemo(
    () => themeSources.flatMap((source) => source.themes),
    [themeSources]
  );
  const themeListLoading = themesLoading || projectThemesLoading;

  // Ref for name input focus
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setName(isFirstOnboarding ? 'New onboarding' : '');
      setPlatform('web');
      setDefaultLanguage('en-US');
      setLanguages(['en-US']);
      setSelectedTheme(null);
      setCustomizedTheme(null);
      setGenerateOpen(false);
      setError(null);
    }
  }, [open, isFirstOnboarding]);

  // Keep wizardStateRef updated with latest values
  useEffect(() => {
    wizardStateRef.current = {
      customizedTheme,
      setCustomizedTheme,
      setCarouselKey,
      setScreenEditorOpen,
      setEditingStep,
    };
  });

  // Auto-select first theme when entering Step 2
  useEffect(() => {
    if (activeStep === 1 && !themeListLoading && orderedThemes.length > 0 && !selectedTheme) {
      setSelectedTheme(orderedThemes[0]);
    }
  }, [activeStep, orderedThemes, selectedTheme, themeListLoading]);

  // Initialize customized theme when entering Step 3
  useEffect(() => {
    if (activeStep === 2 && selectedTheme && !customizedTheme) {
      const cloned = deepClone(selectedTheme);
      setCustomizedTheme(cloned);
    }
  }, [activeStep, selectedTheme, customizedTheme]);

  // Step validations
  const isStep1Valid = name.trim().length > 0 && defaultLanguage !== '';
  const isStep2Valid = selectedTheme !== null;
  const isStep3Valid = true; // Customization is optional

  const isStepValid = useCallback((step: number): boolean => {
    switch (step) {
      case 0: return isStep1Valid;
      case 1: return isStep2Valid;
      case 2: return isStep3Valid;
      default: return false;
    }
  }, [isStep1Valid, isStep2Valid, isStep3Valid]);

  // Compute available locales based on platform
  const availableLocales = useMemo(() => {
    const platformLocales = getLocalesForPlatform(platform);
    const platformCodes = new Set(platformLocales.map(l => l.code));
    const customLocales = localeConfig.locales
      .filter(l => !isKnownLocale(l.code) && !platformCodes.has(l.code));
    return [...platformLocales, ...customLocales];
  }, [platform, localeConfig.locales]);

  // Handle platform change — reset languages that aren't available
  const handlePlatformChange = useCallback((event: SelectChangeEvent<OnboardingPlatform>) => {
    const newPlatform = event.target.value as OnboardingPlatform;
    setPlatform(newPlatform);

    const newAvailable = getLocalesForPlatform(newPlatform);
    const availableCodes = new Set(newAvailable.map(l => l.code));

    const filteredLanguages = languages.filter(code => availableCodes.has(code));

    if (!availableCodes.has(defaultLanguage)) {
      setDefaultLanguage('en-US');
      if (!filteredLanguages.includes('en-US')) {
        filteredLanguages.unshift('en-US');
      }
    }

    setLanguages(filteredLanguages.length > 0 ? filteredLanguages : ['en-US']);
  }, [languages, defaultLanguage]);

  // Handle default language change (auto-add to languages)
  const handleDefaultLanguageChange = useCallback((event: SelectChangeEvent<string>) => {
    const newDefault = event.target.value;
    setDefaultLanguage(newDefault);
    if (newDefault && !languages.includes(newDefault)) {
      setLanguages(prev => [...prev, newDefault]);
    }
  }, [languages]);

  // Handle languages change (ensure default language always included)
  const handleLanguagesChange = useCallback((event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    let newLanguages = typeof value === 'string' ? value.split(',') : value;
    if (defaultLanguage && !newLanguages.includes(defaultLanguage)) {
      newLanguages = [defaultLanguage, ...newLanguages];
    }
    setLanguages(newLanguages);
  }, [defaultLanguage]);

  // Handle V2 theme selection (single selection - radio behavior)
  const handleThemeSelect = useCallback((theme: ThemeV2) => {
    setSelectedTheme(theme);
    setCustomizedTheme(null); // Reset customization
  }, []);

  // Handle theme change from ThemeV2EditorView (Step 3)
  const handleThemeChange = useCallback((updatedTheme: ThemeV2) => {
    setCustomizedTheme(updatedTheme);
  }, []);

  const handleOpenGenerate = useCallback(() => {
    setError(null);
    setGenerateOpen(true);
  }, []);

  const handleApplyGeneratedTheme = useCallback(async (draft: CreateThemeV2Input) => {
    setCustomizedTheme((currentTheme) => createEditableThemeV2FromDraft(draft, {
      baseTheme: currentTheme,
      fallbackId: selectedTheme?.id ?? 'wizard-theme-draft',
      fallbackName: selectedTheme?.name ?? 'Wizard Theme',
      preserveBaseName: true,
    }));
    setCarouselKey((prev) => prev + 1);
  }, [selectedTheme?.id, selectedTheme?.name]);

  // Screen edit — opens V2 editor dialog (Step 3)
  const handleScreenEdit = useCallback((screenId: string, step: StepNode) => {
    // Create FlowStepNode (add entityId for react-flow compatibility)
    const flowStep: FlowStepNode = {
      ...step,
      id: screenId,
      entityId: screenId,
    };
    setEditingStep(flowStep);
    setScreenEditorOpen(true);
  }, []);

  // Step saved — update in customized theme's flowDocument
  // Uses ref to avoid stale closure issues with dialog callbacks
  const handleStepSaved = useMemo(() => {
    return (step: FlowStepNode) => {
      if (!step?.id) return;

      const state = wizardStateRef.current;
      const currentTheme = state.customizedTheme;

      if (!currentTheme) return;

      // Update step in customizedTheme.flowDocument.screens
      const updatedTheme = deepClone(currentTheme);
      if (updatedTheme.flowDocument?.screens) {
        updatedTheme.flowDocument.screens[step.id] = step;
      }

      state.setCustomizedTheme(updatedTheme);
      state.setCarouselKey(prev => prev + 1);
      state.setScreenEditorOpen(false);
      state.setEditingStep(null);
    };
  }, []); // Empty deps — stable function reference

  // Close screen editor dialog
  const handleScreenEditorClose = useCallback(() => {
    setScreenEditorOpen(false);
    setEditingStep(null);
  }, []);

  // Handle CREATE
  const handleCreate = useCallback(async () => {
    if (!defaultLanguage) return;

    setLoading(true);
    setError(null);

    try {
      const finalLanguages = languages.includes(defaultLanguage)
        ? languages
        : [defaultLanguage, ...languages];

      // Extract V2 theme settings from customized theme
      const themeSource = customizedTheme || selectedTheme;
      const themeSettingsV2: OnboardingThemeSettingsV2 | null = themeSource ? themeV2ToOnboardingSettings({
        ...themeSource,
        background: themeSource.background ?? DEFAULT_BACKDROP,
        headerBar: themeSource.headerBar ?? DEFAULT_HEADER_BAR,
        actionBar: themeSource.actionBar ?? DEFAULT_ACTION_BAR,
      }, {
        sourceThemeId: selectedTheme?.id,
      }) : null;

      await onCreate({
        name: name.trim(),
        defaultLanguage,
        languages: finalLanguages,
        themeSettingsV2,
        platform,
      });

      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create onboarding';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [name, defaultLanguage, languages, customizedTheme, selectedTheme, onCreate, onClose, platform]);

  // Handle NEXT
  const handleNext = useCallback(() => {
    if (activeStep === STEPS.length - 1) {
      handleCreate();
    } else {
      setActiveStep(prev => prev + 1);
    }
  }, [activeStep, handleCreate]);

  // Handle BACK
  const handleBack = useCallback(() => {
    setActiveStep(prev => prev - 1);
  }, []);

  // Handle Cancel
  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  // Handle dialog enter animation complete - focus name input
  const handleDialogEntered = useCallback(() => {
    if (activeStep === 0) {
      nameInputRef.current?.focus();
    }
  }, [activeStep]);

  // ============================================================================
  // Render Step Content
  // ============================================================================

  const renderStep1Content = () => (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      pt: 4,
    }}>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
        maxWidth: 500,
      }}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <TextField
          inputRef={nameInputRef}
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          required
          data-testid="wizard-v2-name-input"
        />

        <FormControl fullWidth required>
          <InputLabel id="platform-label-v2">Platform</InputLabel>
          <Select
            labelId="platform-label-v2"
            label="Platform"
            value={platform}
            onChange={handlePlatformChange}
            data-testid="wizard-v2-platform-select"
          >
            <MenuItem value="ios">iOS</MenuItem>
            <MenuItem value="android">Android</MenuItem>
            <MenuItem value="web">Web</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth required>
          <InputLabel id="default-language-label-v2">Default Language</InputLabel>
          <Select
            labelId="default-language-label-v2"
            label="Default Language"
            value={defaultLanguage}
            onChange={handleDefaultLanguageChange}
            data-testid="wizard-v2-default-lang-select"
          >
            {availableLocales.map((locale) => (
              <MenuItem key={locale.code} value={locale.code}>
                {locale.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel id="languages-label-v2">Supported Languages</InputLabel>
          <Select<string[]>
            labelId="languages-label-v2"
            label="Supported Languages"
            multiple
            value={languages}
            onChange={handleLanguagesChange}
            input={<OutlinedInput label="Supported Languages" />}
            data-testid="wizard-v2-languages-select"
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((code) => {
                  const locale = availableLocales.find(l => l.code === code);
                  return (
                    <Chip
                      key={code}
                      label={locale?.label || code}
                      size="small"
                    />
                  );
                })}
              </Box>
            )}
          >
            {availableLocales.map((locale) => (
              <MenuItem key={locale.code} value={locale.code}>
                {locale.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );

  const renderStep2Content = () => (
    <ThemeV2SelectionList
      sources={themeSources}
      selectedThemeId={selectedTheme?.id}
      projectId={projectId}
      loading={themeListLoading}
      hasMore={hasMore}
      loadMore={loadMore}
      loadMoreDisabled={themesLoading}
      onSelect={handleThemeSelect}
    />
  );

  const renderStep3Content = () => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {customizedTheme && (
        <>
          {customizedTheme.generatedMetadata?.sourceKind === 'image-analysis' && (
            <Box sx={{ flexShrink: 0, mb: 2, overflow: 'hidden', borderRadius: 1 }}>
              <GeneratedThemeMetadataPanel theme={customizedTheme} />
            </Box>
          )}
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <ThemeV2EditorView
              theme={customizedTheme}
              onChange={handleThemeChange}
              onScreenEdit={handleScreenEdit}
              disabled={loading}
              carouselKey={carouselKey}
              projectId={projectId}
              settingsPanelFooter={projectId ? (
                <Button
                  variant="outlined"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={handleOpenGenerate}
                  disabled={loading}
                  fullWidth
                  data-testid="wizard-v2-generate-from-image-btn"
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Generate From Image
                </Button>
              ) : undefined}
            />
          </Box>
        </>
      )}
    </Box>
  );

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0: return renderStep1Content();
      case 1: return renderStep2Content();
      case 2: return renderStep3Content();
      default: return null;
    }
  };

  // ============================================================================
  // Main Render
  // ============================================================================

  const activeStepGuideAnchor = WIZARD_STEP_GUIDE_ANCHORS[activeStep];

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      fullScreen
      sx={FULLSCREEN_DIALOG_PAPER_SX}
      TransitionProps={{ onEntered: handleDialogEntered }}
      data-testid="wizard-v2-dialog"
    >
      <Box component="header" sx={FULLSCREEN_DIALOG_HEADER_SX}>
        <Button
          size="small"
          onClick={activeStep === 0 ? handleCancel : handleBack}
          disabled={loading}
          sx={{ ...FULLSCREEN_DIALOG_BUTTON_SX, zIndex: 1 }}
          data-testid="wizard-v2-back-btn"
        >
          Back
        </Button>

        <Box sx={{ ...FULLSCREEN_DIALOG_TITLE_SX, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <WithGuideHelp
            guideKey={activeStepGuideAnchor.guideKey}
            guideService={guideService}
            data-testid={activeStepGuideAnchor.testId}
            position="inline-end"
          >
            <Typography variant="h6" component="h2" noWrap>
              {STEP_TITLES[activeStep]}
            </Typography>
          </WithGuideHelp>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 'auto', zIndex: 1 }}>
          <Button
            size="small"
            variant="contained"
            onClick={handleNext}
            disabled={!isStepValid(activeStep) || loading}
            sx={FULLSCREEN_DIALOG_BUTTON_SX}
            data-testid="wizard-v2-next-btn"
          >
            {loading ? (
              <CircularProgress size={16} color="inherit" />
            ) : activeStep === STEPS.length - 1 ? (
              'Create'
            ) : (
              'Next'
            )}
          </Button>
        </Stack>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1, p: 3 }}>
        <Box sx={{ width: '100%', pt: 2, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
          {/* Stepper */}
          <Stepper activeStep={activeStep} data-testid="wizard-v2-stepper">
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step Content */}
          <Box sx={{ mt: 3, flex: 1, overflow: 'hidden' }}>
            {renderStepContent(activeStep)}
          </Box>
        </Box>
      </Box>

      {/* V2 Screen editor dialog */}
      <OnboardingScreenEditorDialogV2
        open={screenEditorOpen}
        onClose={handleScreenEditorClose}
        stepData={editingStep}
        onStepSaved={handleStepSaved}
        defaultLanguage={customizedTheme?.flowDocument?.defaultLanguage || 'en-US'}
        languages={customizedTheme?.flowDocument?.languages || ['en-US']}
        projectId="wizard-assets"
        onboardingId="wizard-temp"
        versionId="wizard-edit"
        flowDocument={customizedTheme?.flowDocument}
      />
      {projectId && customizedTheme && (
        <GenerateThemeFromImageDialog
          open={generateOpen}
          projectId={projectId}
          baseTheme={customizedTheme}
          createActionLabel="Apply to Wizard"
          onClose={() => setGenerateOpen(false)}
          onCreateTheme={handleApplyGeneratedTheme}
        />
      )}
    </Dialog>
  );
}

export default CreateOnboardingWizardV2;
